'use client'

import { useEffect, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import clsx from 'clsx'
import { useLanguage } from '@/app/(frontend)/providers/LanguageProvider'

type ButtonWithHoverRevealProps = {
  text?: string
  onClick?: () => void
  direction?: 'ltr' | 'rtl'
  disabled?: boolean
}

export function ButtonWithHoverReveal({
  text = 'Explore More',
  onClick,
  direction = 'ltr',
  disabled = false
}: ButtonWithHoverRevealProps) {
  const [hovered, setHovered] = useState(false)
  const { language } = useLanguage()
  const isArabic = language === 'ar'

  return (
    <button
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={clsx(
        'relative inline-flex w-[238px] md:ml-0 items-center justify-center overflow-hidden py-4 md:scale-100 scale-[0.8] text-sm font-semibold bg-[#EE3E25] text-white border-none transition-all focus:scale-95 active:scale-95',
        direction === 'rtl' ? 'flex-row-reverse' : 'flex-row',
        isArabic ? 'pl-4 pr-6 -mr-[12px] md:mr-0' : 'pr-4 pl-6 -ml-[12px] md:ml-0'
      )}
    >
      {/* Sliding overlay */}
      <span
        className={clsx(
          'absolute top-0 bottom-0 z-0 bg-[#F75C3E] transition-all duration-500 ease-in-out',
          hovered ? 'w-[75%]' : 'w-0',
          isArabic ? 'right-0' : 'left-0'
        )}
      />

      {/* Button text */}
      <span className="relative z-10 text-[#F8F8F8] text-[20px] font-[400]">{text}</span>

      {/* Arrow Animation */}
      <span className={clsx('relative size-6', isArabic ? 'mr-6' : 'ml-6')}>
        <ArrowRight
          strokeWidth={1}
          className={clsx(
            'absolute size-6 stroke-white transition-transform duration-500 will-change-transform',
            isArabic ? 'rotate-180 mr-4' : 'ml-1',
            hovered ? (isArabic ? '-translate-x-16' : 'translate-x-16') : 'translate-x-0'
          )}
        />
        <ArrowRight
          strokeWidth={1}
          className={clsx(
            'absolute size-6 stroke-white transition-opacity ease-in',
            isArabic ? 'rotate-180 mr-4' : 'ml-1',
            hovered
              ? 'opacity-100 duration-300 delay-300'
              : 'opacity-0 delay-0 pointer-events-none'
          )}
        />
        <ArrowRight
          strokeWidth={1}
          className={clsx(
            'absolute size-6 stroke-white transition-all ease-in-out',
            isArabic ? 'rotate-180 mr-4' : 'ml-1',
            hovered
              ? 'opacity-100 translate-x-0 duration-500 pointer-events-none'
              : isArabic
                ? 'opacity-0 translate-x-16 duration-500 delay-0'
                : 'opacity-0 -translate-x-16 duration-500 delay-0'
          )}
        />
      </span>
    </button>
  )
}
